import { Box, InputAdornment, SelectChangeEvent } from "@mui/material";
import React, { ChangeEvent, FocusEventHandler, useMemo } from "react";
import { Country } from "../models/Country";
import IOption from "../interfaces/IOption";
import AppSelect from "./AppSelect";
import AppTextField from "./AppTextField";

interface AppPhoneFieldProps {
  countries: Country[];
  countryId?: string | number;
  phone?: string;
  onCountryChange?: (countryId: string) => void;
  onPhoneChange?: (phone: string) => void;
  name?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const DIGIT_SLOTS = new Set(["#", "0", "9"]);

function applyPhoneMask(digits: string, format?: string): string {
  if (!format) {
    return digits;
  }

  let maxDigits = 0;
  for (let i = 0; i < format.length; i++) {
    if (DIGIT_SLOTS.has(format.charAt(i))) {
      maxDigits++;
    }
  }

  const capped = digits.slice(0, maxDigits);
  let result = "";
  let digitIndex = 0;

  for (let i = 0; i < format.length; i++) {
    if (digitIndex >= capped.length) {
      break;
    }

    const char = format.charAt(i);
    if (DIGIT_SLOTS.has(char)) {
      result += capped.charAt(digitIndex);
      digitIndex++;
    } else {
      result += char;
    }
  }

  return result;
}

function AppPhoneField({
  countries,
  countryId,
  phone,
  onCountryChange,
  onPhoneChange,
  name,
  error,
  helperText,
  disabled,
  onBlur,
}: AppPhoneFieldProps) {
  const selectedCountryId = countryId != null ? String(countryId) : "";

  const selectedCountry = useMemo(
    () =>
      countries.find((country) => country.countryId.toString() === selectedCountryId),
    [countries, selectedCountryId],
  );

  const countryOptions: IOption[] = useMemo(
    () =>
      countries.map((country) => ({
        label: `${country.countryAlpha2} ${country.countryCode}`,
        value: country.countryId.toString(),
      })),
    [countries],
  );

  const displayPhone = applyPhoneMask(
    (phone ?? "").replace(/\D/g, ""),
    selectedCountry?.phoneCountryFormat,
  );

  const handleCountryChange = (event: SelectChangeEvent<any>) => {
    const nextCountryId = String(event.target.value);
    const nextCountry = countries.find(
      (country) => country.countryId.toString() === nextCountryId,
    );
    const digits = (phone ?? "").replace(/\D/g, "");

    onCountryChange?.(nextCountryId);
    onPhoneChange?.(applyPhoneMask(digits, nextCountry?.phoneCountryFormat));
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    onPhoneChange?.(
      applyPhoneMask(digits, selectedCountry?.phoneCountryFormat),
    );
  };

  return (
    <AppTextField
      name={name}
      error={error}
      helperText={helperText}
      disabled={disabled}
      placeholder={selectedCountry?.phoneCountryFormat}
      value={displayPhone}
      onChange={handlePhoneChange}
      onBlur={onBlur}
      inputProps={{ inputMode: "numeric" }}
      sx={{
        "&:focus-within .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "error.main" : "primary.main",
          borderWidth: "2px",
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              mr: 1,
              ml: -0.5,
              height: "100%",
              maxHeight: "none",
              alignItems: "center",
            }}
          >
            <AppSelect
              name={name ? `${name}CountryId` : undefined}
              isSearchable
              disabled={disabled}
              value={selectedCountryId}
              options={countryOptions}
              onChange={handleCountryChange}
              sx={{
                marginBottom: 0,
                bgcolor: "transparent",
                minWidth: 88,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-select": {
                  padding: "0 24px 0 8px",
                },
              }}
            />
            <Box
              aria-hidden
              sx={{
                width: "1px",
                height: 20,
                bgcolor: "rgba(0, 0, 0, 0.23)",
                mx: 0.75,
                flexShrink: 0,
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );
}

export default AppPhoneField;
